import React, { useId, useState } from 'react';
import { Box, Button, Checkbox, Chip, FormControlLabel, IconButton, Popover, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { sortByNumber } from '../data_table/sorting';
import Skeleton from '../ui/Skeleton';
import Tooltip from '../ui/Tooltip';
import { OWN_GROUP_METRICS } from './useWatchlistOwnGroupPreferences';

const EMPTY = Object.freeze({});

const METRIC_HELP = {
    beta: {
        title: 'Own-group beta',
        description: 'The slope of a regression of this security’s daily return on its own category’s daily return, with an intercept.',
        formula: 'rᵢ,ₜ = αᵢ + βᵢ bₜ + εᵢ,ₜ\nβᵢ = Σₜ∈P [(bₜ − b̄)(rᵢ,ₜ − r̄ᵢ)] / Σₜ∈P (bₜ − b̄)²',
        interpretation: 'A beta of 1.5 means a 1 percentage-point change in the group return is associated with a 1.5 percentage-point change in the security return on the fitted line. Negative beta indicates an inverse relationship in this sample.',
        requirement: 'Requires at least 60 paired observations, sufficient coverage and nonzero group-return variation. The cell tooltip includes a 95% beta interval using HAC standard errors with five observation lags.',
    },
    selloff: {
        title: 'Selloff capture',
        description: 'The ratio of the security’s average return to the group’s average return on the same dates when the group fell by at least 2%.',
        formula: 'D = {t ∈ P : bₜ ≤ −0.02}\nCaptureᵢ = [(1 / |D|) Σₜ∈D rᵢ,ₜ] / [(1 / |D|) Σₜ∈D bₜ]',
        interpretation: 'For example, an average security return of −4.5% divided by an average group return of −3% gives 1.5× capture. A negative value means the security rose on average on these group selloff dates.',
        requirement: 'Requires at least 10 paired selloff observations in addition to the 60-pair and coverage requirements. This is a ratio of mean returns, not the average of daily ratios.',
    },
    r_squared: {
        title: 'Explained variation (R²)',
        description: 'The share of the security’s observed daily return variation explained by the fitted own-group regression.',
        formula: 'r̂ᵢ,ₜ = αᵢ + βᵢ bₜ\nR² = 1 − Σₜ∈P (rᵢ,ₜ − r̂ᵢ,ₜ)² / Σₜ∈P (rᵢ,ₜ − r̄ᵢ)²',
        interpretation: 'An R² of 80% means the fitted group regression explains 80% of the security’s return variation within this sample. It does not measure forecast confidence or establish causation.',
        requirement: 'Uses the same paired observations and availability requirements as beta. R² is undefined when the security’s returns have no variation.',
    },
    paired: {
        title: 'Paired observations',
        description: 'The number of model-date observations with verified returns for both this security and its group benchmark.',
        formula: 'P = {t : rᵢ,ₜ and bₜ are both available}\nn = |P|',
        interpretation: 'This is the sample size used for beta and R², not the number of elapsed calendar days. Selloff capture uses only the qualifying subset D of these paired dates.',
        requirement: 'The count can be shown even when there are too few observations to estimate a statistic. Fewer than 60 pairs makes beta, R² and selloff capture unavailable.',
    },
};

function MetricFormula({ children }) {
    return <Box sx={{ px: 1.25, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'action.hover' }}>
        <Typography component="div" variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {children}
        </Typography>
    </Box>;
}

export function OwnGroupAttributeControls({ definition, preference = EMPTY, onChange, disabled }) {
    const id = useId();
    const [anchor, setAnchor] = useState(null);
    const [help, setHelp] = useState(null);
    const selected = OWN_GROUP_METRICS.filter(({ id: metric }) => preference[metric] === true);
    const explanation = help ? METRIC_HELP[help.metric] : null;
    const infoButton = (metric) => <IconButton size="small"
        aria-label={`About ${METRIC_HELP[metric].title} for ${definition.display_name}`}
        aria-haspopup="dialog" aria-expanded={help?.metric === metric}
        aria-controls={help?.metric === metric ? `${id}-help` : undefined}
        sx={{ p: 0.375, color: 'text.secondary' }}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
        }}
        onClick={(event) => {
            event.stopPropagation();
            setHelp({ metric, anchor: event.currentTarget });
        }}>
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
    </IconButton>;
    return <Box sx={{ minWidth: 0, width: '100%' }}>
        <Button variant="outlined" size="small" fullWidth disabled={disabled}
            aria-label={`Within-group statistics for ${definition.display_name}`}
            aria-haspopup="dialog" aria-expanded={Boolean(anchor)}
            aria-controls={anchor ? `${id}-statistics` : undefined}
            title={selected.map(({ description }) => description).join(', ') || 'Select within-group statistics'}
            endIcon={<ExpandMoreIcon />}
            onClick={(event) => setAnchor(event.currentTarget)}
            sx={{ minHeight: 40, textTransform: 'none', justifyContent: 'space-between',
                borderColor: 'divider', color: 'text.primary', px: 1, minWidth: 0,
                '& .MuiButton-endIcon': { flexShrink: 0, ml: 0.5 } }}>
            <Stack component="span" direction="row" spacing={0.5} sx={{ minWidth: 0, overflow: 'hidden', alignItems: 'center' }}>
                {selected.length ? <>
                    {selected.slice(0, 2).map(({ id: metric, label }) => <Chip component="span" key={metric}
                        size="small" label={label} variant="outlined"
                        sx={{ maxWidth: 145, height: 25, borderRadius: 1, pointerEvents: 'none',
                            '& .MuiChip-label': { px: 0.75 } }} />)}
                    {selected.length > 2 && <Typography component="span" variant="caption" sx={{ flexShrink: 0 }}>
                        +{selected.length - 2}
                    </Typography>}
                </> : <Typography component="span" variant="body2" color="text.secondary" noWrap>Select statistics</Typography>}
            </Stack>
        </Button>
        <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ id: `${id}-statistics`, role: 'dialog',
                'aria-label': `Within-group statistics for ${definition.display_name}`,
                sx: { width: 330, maxWidth: 'calc(100vw - 32px)', border: '1px solid',
                    borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', p: 1.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2">Within-group statistics</Typography>
                <IconButton size="small" aria-label="Close statistics selector" onClick={() => setAnchor(null)}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Stack>
            {OWN_GROUP_METRICS.map(({ id: metric, description }) => <Stack key={metric} direction="row"
                alignItems="center" justifyContent="space-between">
                <FormControlLabel label={description} sx={{ m: 0, flex: 1, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    control={<Checkbox size="small" checked={preference[metric] === true} disabled={disabled}
                        inputProps={{ 'aria-label': `${description} for ${definition.display_name}` }}
                        onChange={(event) => onChange(definition.attribute_definition_id, Object.fromEntries(
                            OWN_GROUP_METRICS.map(({ id: key }) => [key, key === metric ? event.target.checked : preference[key] === true]),
                        ))} />} />
                {infoButton(metric)}
            </Stack>)}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Selections take effect when you save changes in the attribute editor.
            </Typography>
        </Popover>
        <Popover open={Boolean(help)} anchorEl={help?.anchor} onClose={() => setHelp(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ id: `${id}-help`, role: 'dialog', 'aria-labelledby': `${id}-help-title`,
                'aria-describedby': `${id}-help-description`,
                sx: { width: 490, maxWidth: 'calc(100vw - 32px)', maxHeight: '75vh', overflowY: 'auto',
                    border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' } }}>
            {explanation && <Stack spacing={1.25} sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                    <Typography id={`${id}-help-title`} component="h3" variant="subtitle1" fontWeight={600}>{explanation.title}</Typography>
                    <IconButton size="small" aria-label="Close metric explanation" onClick={() => setHelp(null)}><CloseIcon fontSize="small" /></IconButton>
                </Stack>
                <Typography id={`${id}-help-description`} variant="body2">{explanation.description}</Typography>
                <MetricFormula>{explanation.formula}</MetricFormula>
                <Typography variant="body2">{explanation.interpretation}</Typography>
                <Typography variant="body2" color="text.secondary">{explanation.requirement}</Typography>
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.25 }}>
                    <Typography variant="subtitle2" gutterBottom>The shared group benchmark</Typography>
                    <MetricFormula>{'bₜ = (1 / M) Σⱼ∈group rⱼ,ₜ'}</MetricFormula>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        rᵢ,ₜ is this security’s adjusted USD return; bₜ is the equal-weight return of the M securities in its current category.
                        Every member uses the same common-session endpoints from stored Yahoo adjusted-price and FX history.
                        The security is included in its own benchmark. Current saved membership is replayed over the available six-month history.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        P contains dates with both returns available; overbars denote averages over P, and |P| and |D| count dates. A group needs at least two members,
                        with complete member returns on at least 80% of analysis dates. A security needs pairs on at least 80% of
                        eligible benchmark dates. Missing returns are not filled with zero and missing members are not removed or reweighted.
                        Actual dates and sample counts appear in the cell tooltip. These statistics describe historical relationships.
                    </Typography>
                </Box>
            </Stack>}
        </Popover>
    </Box>;
}

const REASONS = {
    attribute_membership_unassigned: 'No category is assigned to this security.',
    benchmark_members_insufficient: 'A category needs at least two accepted securities.',
    security_observations_insufficient: 'Fewer than 60 paired observations.',
    security_coverage_insufficient: 'Security history covers less than 80% of benchmark dates.',
    benchmark_coverage_insufficient: 'Complete group history covers less than 80% of analysis dates.',
    selloff_observations_insufficient: 'Fewer than 10 paired selloff observations.',
    security_variance_zero: 'This security has no observed return variation; R² is undefined.',
};
const reasonText = (value) => REASONS[value] || (typeof value === 'string'
    ? `${value.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase())}.` : 'The statistic is unavailable.');
const DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
});
const date = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value || '')
    && Number.isFinite(new Date(`${value}T00:00:00Z`).getTime())
    ? DATE_FORMAT.format(new Date(`${value}T00:00:00Z`)) : '';
const formatMetric = (metric, value) => !Number.isFinite(value) ? '—'
    : metric === 'paired' ? String(value) : metric === 'r_squared' ? `${(100 * value).toFixed(1)}%`
        : `${value.toFixed(2)}${metric === 'selloff' ? '×' : ''}`;

function metricDetails(state, attributeId, cogniveId, metric) {
    const attribute = state?.attributes?.get(attributeId);
    const row = attribute?.rowsById.get(cogniveId);
    const group = row?.group_index == null ? null : attribute.groups[row.group_index];
    const result = metric === 'selloff' ? row?.selloff : row?.regression;
    const available = metric === 'paired' ? Boolean(group) : result?.status === 'available';
    const raw = metric === 'paired' ? row?.paired_observation_count : metric === 'selloff'
        ? result?.capture : metric === 'r_squared' ? result?.r_squared : result?.beta;
    const value = !state?.loading && !state?.unavailable && available && Number.isFinite(raw) ? raw : null;
    return { row, group, result, value };
}

const SOURCE_NAMES = { barra: 'Barra', axioma: 'Axioma', yahoo: 'Yahoo' };
const sourceLabel = (value) => typeof value === 'string'
    ? value.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase()) : '';
const sourceDetails = (source) => source ? [SOURCE_NAMES[source.vendor] || sourceLabel(source.vendor),
    source.model, source.currency, ['vendor_adjusted_security_return', 'usd_total_return_level_ratio'].includes(source.return_basis)
        ? 'Adjusted returns' : sourceLabel(source.return_basis)].filter(Boolean).join(' · ') : '';

export function ownGroupCell(state, attributeId, cogniveId, metric) {
    const { row, group, result, value } = metricDetails(state, attributeId, cogniveId, metric);
    const globallyUnavailable = Boolean(state?.loading || state?.unavailable);
    const reason = state?.loading ? 'Loading own-group statistics.' : state?.unavailable
        || (value == null ? reasonText(result?.reason || (!group ? 'attribute_membership_unassigned' : null)) : '');
    const firstDate = date(row?.first_paired_date);
    const lastDate = date(row?.last_paired_date);
    const dates = firstDate && lastDate ? firstDate === lastDate ? firstDate : `${firstDate} → ${lastDate}` : '';
    const paired = row?.paired_observation_count;
    const observations = Number.isFinite(paired) && (!globallyUnavailable || paired > 0)
        ? [dates, `${paired} paired observations`].filter(Boolean).join(' · ') : '';
    const beta = row?.regression;
    const selloff = row?.selloff?.usable_selloff_count;
    const source = sourceDetails(state?.data?.source);
    const lines = [reason,
        group ? `Category: ${group.group_value} · ${group.member_count} members` : row ? 'Category: Unassigned' : '',
        observations,
        metric === 'beta' && value != null && Number.isFinite(beta?.beta_ci_lower) && Number.isFinite(beta?.beta_ci_upper)
            ? `95% beta interval: ${beta.beta_ci_lower.toFixed(2)} to ${beta.beta_ci_upper.toFixed(2)}` : '',
        metric === 'selloff' && Number.isFinite(selloff) && (!globallyUnavailable || selloff > 0)
            ? `${selloff} paired selloff observations` : '',
        source ? `Source: ${source}` : '',
    ].filter(Boolean);
    return { value, text: formatMetric(metric, value), tooltip: lines.join('\n') };
}

export function buildOwnGroupColumns(definitions, preferences, state) {
    if (!state?.supported) return [];
    return definitions.flatMap((definition) => OWN_GROUP_METRICS.filter(
        ({ id }) => preferences[definition.attribute_definition_id]?.[id] === true,
    ).map(({ id: metric, label }) => ({
            id: `own_group:${definition.attribute_definition_id}:${metric}`,
            header: `${label} · ${definition.display_name}`,
            size: metric === 'paired' ? 190 : 150,
            accessorFn: (row) => metricDetails(state, definition.attribute_definition_id, row.cognive_id, metric).value,
            sortFn: sortByNumber,
            meta: { align: 'right', formatText: (value) => formatMetric(metric, value) },
            cell: ({ row }) => {
                const cell = ownGroupCell(state, definition.attribute_definition_id, row.original.cognive_id, metric);
                return <Tooltip content={<span className="whitespace-pre-line">{cell.tooltip}</span>}>
                    <span className="block w-full" aria-busy={state.loading || undefined}>
                        {state.loading ? <Skeleton width="70%" className="ml-auto" /> : cell.text}
                    </span>
                </Tooltip>;
            },
        })));
}
